<template>
    <v-app>
        <v-container fluid>
            <budget-revision-setup :dialog="dialog" :data="data" @update:dialog="dialog = $event" @save-revision="saveRevisionHandler" />

            <v-row>
                <v-col cols="12">
                    <v-expansion-panels v-model="panel">
                        <v-expansion-panel>
                            <v-expansion-panel-title color="blue-grey-lighten-4" class="panel-header">Budget Revision
                                List</v-expansion-panel-title>
                            <v-expansion-panel-text>
                                <v-row class="py-2">
                                    <v-col md="4" cols="12">
                                        <v-text-field v-model="search" append-inner-icon="mdi-magnify" label="Search"
                                            single-line hide-details density="compact" variant="outlined"
                                            bg-color="grey-lighten-4" class="pl-2" clearable></v-text-field>
                                    </v-col>

                                    <v-col md="5" cols="12">
                                        <div class="d-flex align-center flex-column">
                                            <v-btn-group variant="outlined" density="compact" size="x-small" divided>
                                                <v-btn prepend-icon="mdi mdi-download" variant="flat" color="success"
                                                    @click="exportToCSV">CSV</v-btn>
                                                <v-btn prepend-icon="mdi mdi-download" variant="flat" color="primary"
                                                    @click="exportToExcel">EXCEL</v-btn>
                                                <v-btn prepend-icon="mdi mdi-download" variant="flat" color="red"
                                                    @click="exportToPDF">PDF</v-btn>
                                            </v-btn-group>
                                        </div>
                                    </v-col>

                                    <v-col md="3" cols="12">
                                        <div class="text-right pr-2">
                                            <v-btn v-if="$insertAccess($route.path)"
                                                @click="openAddRevision" color="primary" variant="tonal"
                                                prepend-icon="mdi mdi-plus" rounded="xl">
                                                Add Revision
                                            </v-btn>
                                        </div>
                                    </v-col>
                                </v-row>

                                <v-data-table :headers="headers" :items="getProjectBudgetRevisionList" :loading="loading"
                                    v-model:items-per-page="itemsPerPage" :search="search" :hover="true"
                                    density="compact">

                                    <template v-slot:item.index="{ index }">
                                        {{ ++index }}
                                    </template>

                                    <template v-slot:item.current_budget_amount="{ item }">
                                        {{ formatCurrency(item.current_budget_amount) }}
                                    </template>

                                    <template v-slot:item.new_budget_amount="{ item }">
                                        {{ formatCurrency(item.new_budget_amount) }}
                                    </template>

                                    <template v-slot:item.revision_date="{ item }">
                                        {{ formatDate(item.revision_date) }}
                                    </template>

                                    <template v-slot:item.status="{ item }">
                                        <v-chip v-if="item.status == 1 || item.status == '1'" color="success" size="small"
                                            class="text-capitalize">Active</v-chip>
                                        <v-chip v-else color="red" size="small"
                                            class="text-capitalize">Inactive</v-chip>
                                    </template>

                                    <template v-slot:item.revision_status="{ item }">
                                        <v-chip v-if="item.revision_status == 'approved'" color="success" size="small"
                                            class="text-capitalize">Approved</v-chip>
                                        <v-chip v-else-if="item.revision_status == 'draft'" color="warning" size="small"
                                            class="text-capitalize">Draft</v-chip>
                                        <v-chip v-else color="grey" size="small"
                                            class="text-capitalize">{{ item.revision_status || 'Pending' }}</v-chip>
                                    </template>

                                    <template v-slot:item.approved_date="{ item }">
                                        {{ formatDate(item.approved_date) }}
                                    </template>

                                    <template v-slot:item.actions="{ item }">
                                        <v-icon class="me-2 edit-button" color="primary" @click="editItem(item)"
                                            v-if="$updateAccess($route.path)">
                                            mdi mdi-pencil-outline
                                        </v-icon>
                                        <v-icon class="me-2 edit-button" color="red" @click="deleteItem(item)"
                                            v-if="$updateAccess($route.path)">
                                            mdi mdi-delete-outline
                                        </v-icon>
                                    </template>
                                </v-data-table>
                            </v-expansion-panel-text>
                        </v-expansion-panel>
                    </v-expansion-panels>
                </v-col>
            </v-row>
        </v-container>
    </v-app>
</template>

<script>
import Axios from 'axios';
import { deleteProject } from '@/store/api';
import { toast } from "vue3-toastify";
import BudgetRevisionSetup from "@/components/module/project/budgetManagement/budgetRevision.vue";

export default {
    components: {
        BudgetRevisionSetup,
    },
    data: () => ({
        itemsPerPage: 50,
        panel: [0],
        loading: false,
        search: "",
        dialog: false,
        data: {},
        headers: [
            { title: "#SL", align: "center", key: "index", width: "7%", sortable: false },
            { title: "Revision No", key: "revision_no", align: "left", sortable: false },
            { title: "Project Budget ID", key: "project_budget_id", align: "left", sortable: false },
            { title: "Project Total Budget (Old)", key: "current_budget_amount", align: "right", sortable: false },
            { title: "Project Total Budget (New)", key: "new_budget_amount", align: "right", sortable: false },
            { title: "Revision Date", key: "revision_date", align: "left", sortable: false },
            { title: "Status", align: "center", key: "status", sortable: true },
            { title: "Revision Status", align: "center", key: "revision_status", sortable: true },
            { title: "Approved By", key: "approved_by", align: "left", sortable: false },
            { title: "Approved Date", key: "approved_date", align: "left", sortable: false },
            { title: "Actions", align: "center", key: "actions", sortable: false }
        ],
    }),
    watch: {},
    created() {
        if (this.$store.getters.getProjectBudgetRevisionList.length == 0) {
            this.loading = true;
            this.$store.dispatch('getProjectBudgetRevisionList').then((response) => {
                this.loading = false;
            }).catch(() => {
                this.loading = false;
            });
        }
    },
    computed: {
        getProjectBudgetRevisionList() {
            return this.$store.getters.getProjectBudgetRevisionList.map(item => ({
                ...item,
                project_budget_id: item.budget?.code || '', // Flatten budget code
                current_budget_amount: item.project_total_budget_old, // Map to expected key
                new_budget_amount: item.project_total_budget_new, // Map to expected key
            }));
        },
    },
    methods: {
        openAddRevision() {
            this.data = {
                revision_no: "",
                project_budget_id: "",
                current_budget_amount: "",
                new_budget_amount: "",
                revision_date: "",
                status: "1",
                revision_id: null,
            };
            this.dialog = true;
        },
        saveRevisionHandler(revision) {
            // Handle save callback if needed
        },
        editItem(item) {
            this.data = { ...item, id: item.id };
            this.dialog = true;
        },
        deleteItem(item) {
            if (confirm('Are you sure you want to delete this budget revision?')) {
                // TODO: Implement delete functionality when we have delete API
                toast.info('Delete functionality will be implemented when API is available');
            }
        },
        formatCurrency(amount) {
            return new Intl.NumberFormat('en-US', {
                style: 'currency',
                currency: 'USD'
            }).format(amount || 0);
        },
        formatDate(date) {
            if (!date) return '';
            return new Date(date).toLocaleDateString();
        },
        exportToPDF() {
            let serialNumber = 1;
            const columns = ['#SL', 'Revision No', 'Project Budget ID', 'Project Total Budget (Old)', 'Project Total Budget (New)', 'Revision Date', 'Status', 'Approved By', 'Approved Date'];
            const rows = this.getProjectBudgetRevisionList.map(item => [
                serialNumber++,
                item.revision_no,
                item.project_budget_id,
                this.formatCurrency(item.current_budget_amount),
                this.formatCurrency(item.new_budget_amount),
                this.formatDate(item.revision_date),
                item.revision_status == 1 ? 'Active' : 'Inactive',
                item.revision_status == 'approved' ? 'Approved' : item.revision_status == 'draft' ? 'Draft' : item.revision_status == 'Pending' ? 'Pending' : 'Cancelled',
                item.approved_by || '',
                this.formatDate(item.approved_date)
            ]);
            this.$exportTableToPDF(columns, rows, 'Budget Revision List', 'budget_revision_list_' + this.$fileDate())
        },
        exportToExcel() {
            var data = [['#SL', 'Revision No', 'Project Budget ID', 'Project Total Budget (Old)', 'Project Total Budget (New)', 'Revision Date', 'Status', 'Revision Status', 'Approved By', 'Approved Date']];
            var serialNumber = 1;
            this.getProjectBudgetRevisionList.forEach(item => {
                data.push([
                    serialNumber++,
                    item.revision_no,
                    item.project_budget_id,
                    item.current_budget_amount,
                    item.new_budget_amount,
                    this.formatDate(item.revision_date),
                    item.revision_status == 1 ? 'Active' : 'Inactive',
                    item.approved_by || '',
                    this.formatDate(item.approved_date)
                ]);
            });
            this.$exportTableToExcel(data, 'budget_revision_list_' + this.$fileDate());
        },
        exportToCSV() {
            var data = [['#SL', 'Revision No', 'Project Budget ID', 'Project Total Budget (Old)', 'Project Total Budget (New)', 'Revision Date', 'Status', 'Approved By', 'Approved Date']];
            var serialNumber = 1;
            this.getProjectBudgetRevisionList.forEach(item => {
                data.push([
                    serialNumber++,
                    item.revision_no,
                    item.project_budget_id,
                    item.current_budget_amount,
                    item.new_budget_amount,
                    this.formatDate(item.revision_date),
                    item.revision_status == 1 ? 'Active' : 'Inactive',
                    item.approved_by || '',
                    this.formatDate(item.approved_date)
                ]);
            });
            this.$exportTableToCSV(data, 'budget_revision_list_' + this.$fileDate());
        },
    },
    mounted() {}
}
</script>